import { useQuery } from '@tanstack/react-query';
import { Spin, Alert } from 'antd';
import { getPermissionTree, roleKeys } from '../../../../api/role.api';
import type { PermissionTree as PermissionTreeType } from '../../../../types/role.types';
import PermissionGroupNode from './PermissionGroupNode';

interface PermissionTreeProps {
	selectedPermissions: Set<string>;
	onTogglePermission: (value: string) => void;
	onToggleGroup: (group: PermissionTreeType) => void;
	getGroupState: (group: PermissionTreeType) => { checked: boolean; indeterminate: boolean };
	disabled?: boolean;
}

export default function PermissionTree({
	selectedPermissions,
	onTogglePermission,
	onToggleGroup,
	getGroupState,
	disabled = false,
}: PermissionTreeProps) {
	const {
		data: tree,
		isLoading,
		error,
	} = useQuery({
		queryKey: roleKeys.permissionTree(),
		queryFn: getPermissionTree,
		staleTime: 10 * 60 * 1000,
	});

	if (isLoading) {
		return (
			<div className="flex justify-center py-12">
				<Spin size="large" />
			</div>
		);
	}

	if (error) {
		return <Alert type="error" message="Failed to load permissions" showIcon />;
	}

	if (!tree || tree.length === 0) {
		return <Alert type="info" message="No permissions available" showIcon />;
	}

	const sorted = tree.slice().sort((a, b) => String(a.position).localeCompare(String(b.position)));

	return (
		<div>
			{sorted.map((group) => (
				<PermissionGroupNode
					key={group.value}
					group={group}
					selectedPermissions={selectedPermissions}
					onTogglePermission={onTogglePermission}
					onToggleGroup={onToggleGroup}
					getGroupState={getGroupState}
					disabled={disabled}
				/>
			))}
		</div>
	);
}
